import DeleteTestUser from "./DeleteTestUser";
import { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Col, Container, Form, Nav, Row, Spinner } from "react-bootstrap";
import ScannerDataCenter from './ScannerDataCenter';
import CatalogManager from './CatalogManager';
import MeetupReviewManager from './MeetupReviewManager';
import AdminBroadcastManager from './AdminBroadcastManager';
import { Link } from "react-router-dom";
import api, { mediaUrl } from "../../api/axios";
const metricLabels = {
  registered_users: "Usuarios registrados",
  registered_last_7_days: "Altas ultimos 7 dias",
  onboarding_completed_users: "Onboarding completo",
  completed_binder_users: "Binders completos",
  returned_week_2_users: "Vuelven semana 2",
  users_who_posted: "Usuarios que publican",
  total_posts: "Posts totales",
  total_deals: "Acuerdos totales",
  completed_deals: "Acuerdos completados",
  total_tournaments: "Eventos",
  tournament_registrations: "Asistencias"
};

const userActivation = user => {
  if (!user.email_verified_at) return { label: "Correo pendiente", bg: "warning", text: "dark" };
  if (!Number(user.binder_count)) return { label: "Sin binder", bg: "secondary" };
  if (!Number(user.card_count)) return { label: "Sin cartas", bg: "info", text: "dark" };
  return { label: "Activado", bg: "success" };
};

const compactDate = value => new Date(value).toLocaleDateString("es-ES", { day: "2-digit", month: "short" });

function MetricsDashboard({ metrics }) {
  const daily = metrics.dailyActivity || [];
  const maxActive = Math.max(1, ...daily.map(item => Number(item.active_users || 0)));
  const registered = Math.max(1, Number(metrics.registered_users || 0));
  const engagement = metrics.engagement || {};

  return (
    <div className="admin-metrics-dashboard">
      <div className="metrics-section-head">
        <div><h2>Salud de Cardora</h2><p>Uso real y recurrencia de la comunidad.</p></div>
        {metrics.generatedAt && <small>Actualizado {new Date(metrics.generatedAt).toLocaleString("es-ES")}</small>}
      </div>
      <div className="metrics-kpi-grid">
        {[
          ["Usuarios activos hoy", engagement.dau, "bi-lightning-charge", "Hoy"],
          ["Activos en 7 dias", engagement.wau, "bi-calendar-week", "WAU"],
          ["Activos en 30 dias", engagement.mau, "bi-people", "MAU"],
          ["Frecuencia semanal", `${engagement.wauMauRate || 0}%`, "bi-arrow-repeat", "WAU / MAU"]
        ].map(([label, value, icon, hint]) => (
          <div className="metrics-kpi-card" key={label}>
            <span className="metrics-kpi-icon"><i className={`bi ${icon}`} /></span>
            <div><small>{hint}</small><strong>{value ?? 0}</strong><span>{label}</span></div>
          </div>
        ))}
      </div>

      <div className="metrics-layout-grid">
        <Card className="cardora-card metrics-chart-card">
          <Card.Body>
            <div className="metrics-card-heading"><div><Card.Title>Actividad diaria</Card.Title><p>Usuarios activos durante los ultimos 30 dias.</p></div><Badge bg="light" text="dark">30 dias</Badge></div>
            <div className="metrics-chart" aria-label="Actividad diaria de usuarios">
              {daily.map((item, index) => (
                <div className="metrics-chart-column" key={item.day} title={`${compactDate(item.day)}: ${item.active_users} activos, ${item.new_users} altas, ${item.cards_added} cartas, ${item.matches} matches`}>
                  <span className="metrics-chart-value">{item.active_users || ""}</span>
                  <span className="metrics-chart-bar" style={{ height: `${Math.max(4, (Number(item.active_users || 0) / maxActive) * 100)}%` }} />
                  {(index === 0 || index === daily.length - 1 || index % 7 === 0) && <small>{compactDate(item.day)}</small>}
                </div>
              ))}
            </div>
            <div className="metrics-chart-summary">
              <span><i className="bi bi-person-plus" /> {daily.reduce((sum, item) => sum + Number(item.new_users || 0), 0)} altas</span>
              <span><i className="bi bi-collection" /> {daily.reduce((sum, item) => sum + Number(item.cards_added || 0), 0)} cartas</span>
              <span><i className="bi bi-stars" /> {daily.reduce((sum, item) => sum + Number(item.matches || 0), 0)} matches</span>
            </div>
          </Card.Body>
        </Card>

        <Card className="cardora-card">
          <Card.Body>
            <div className="metrics-card-heading"><div><Card.Title>Embudo de activacion</Card.Title><p>Donde se detienen los usuarios.</p></div></div>
            <div className="metrics-funnel">
              {(metrics.activationFunnel || []).map(item => {
                const percentage = Math.round((Number(item.value || 0) / registered) * 100);
                return <div className="metrics-funnel-row" key={item.key}>
                  <div><span>{item.label}</span><strong>{item.value} <small>{percentage}%</small></strong></div>
                  <div className="metrics-funnel-track"><span style={{ width: `${percentage}%` }} /></div>
                </div>;
              })}
            </div>
          </Card.Body>
        </Card>
      </div>

      <div className="metrics-layout-grid metrics-layout-grid-bottom">
        <Card className="cardora-card">
          <Card.Body>
            <div className="metrics-card-heading"><div><Card.Title>Retencion por semana</Card.Title><p>Usuarios que volvieron entre los dias 7 y 14.</p></div></div>
            <div className="table-responsive">
              <table className="table metrics-cohort-table mb-0">
                <thead><tr><th>Semana</th><th>Altas</th><th>Volvieron</th><th>Retencion</th></tr></thead>
                <tbody>
                  {(metrics.retentionCohorts || []).map(row => <tr key={row.cohort_week}><td>{compactDate(row.cohort_week)}</td><td>{row.signups}</td><td>{row.returned_week_1}</td><td><strong>{row.retentionRate}%</strong></td></tr>)}
                  {!metrics.retentionCohorts?.length && <tr><td colSpan="4" className="muted-copy">Todavia no hay cohortes suficientes.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>

        <Card className="cardora-card">
          <Card.Body>
            <div className="metrics-card-heading"><div><Card.Title>Usuarios para acompanar</Card.Title><p>Cuentas que pueden necesitar una ayuda para empezar.</p></div><Badge bg={(metrics.attentionUsers || []).length ? "warning" : "success"} text="dark">{(metrics.attentionUsers || []).length}</Badge></div>
            <div className="metrics-attention-list">
              {(metrics.attentionUsers || []).slice(0, 8).map(user => <div className="metrics-attention-row" key={user.id}>
                <div><Link to={`/u/${user.handle || user.id}`}><strong>{user.display_name || user.email}</strong></Link><small>{user.reason}</small></div>
                <span>{user.binder_count} binders · {user.card_count} cartas</span>
              </div>)}
              {!metrics.attentionUsers?.length && <span className="muted-copy">No hay usuarios estancados.</span>}
            </div>
          </Card.Body>
        </Card>
      </div>

      <Card className="cardora-card metrics-sessions-card">
        <Card.Body>
          <div className="metrics-card-heading">
            <div><Card.Title>Sesiones recientes</Card.Title><p>Usuarios con una sesion registrada en los ultimos 30 dias.</p></div>
            <Badge bg="light" text="dark">{(metrics.recentSessions || []).length}</Badge>
          </div>
          <div className="metrics-session-list">
            {(metrics.recentSessions || []).map(user => (
              <div className="metrics-session-row" key={user.id}>
                <Link to={`/u/${user.handle || user.id}`}>
                  <strong>{user.display_name || user.handle || "Usuario"}</strong>
                  {user.handle && <small>@{user.handle}</small>}
                </Link>
                <time dateTime={user.last_session}>{new Date(user.last_session).toLocaleString("es-ES")}</time>
              </div>
            ))}
            {!metrics.recentSessions?.length && <span className="muted-copy">Todavia no hay sesiones registradas en este periodo.</span>}
          </div>
        </Card.Body>
      </Card>

      <Card className="cardora-card metrics-history-card">
        <Card.Body><Card.Title>Acumulado historico</Card.Title><div className="metrics-history-grid">{Object.entries(metricLabels).map(([key, label]) => <div key={key}><span>{label}</span><strong>{metrics[key] ?? 0}</strong></div>)}</div></Card.Body>
      </Card>
    </div>
  );
}
function SuperAdminDashboard() {
  const [activeTab, setActiveTab] = useState("metrics");
  const [metrics, setMetrics] = useState(null);
  const [overview, setOverview] = useState({ users: [], businesses: [], tournaments: [], invitations: [] });
  const [userDirectory, setUserDirectory] = useState({ users: [], total: 0, page: 1, limit: 25 });
  const [userFilters, setUserFilters] = useState({ search: "", role: "all" });
  const [appliedUserFilters, setAppliedUserFilters] = useState({ search: "", role: "all" });
  const [usersLoading, setUsersLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [operationalAudits, setOperationalAudits] = useState({ emails: [], deletions: [], failedRegistrations: [] });
  const [modules, setModules] = useState([]);
  const [businessModules, setBusinessModules] = useState([]);
  const [scannerDataCenter, setScannerDataCenter] = useState(null);
  const [brandSettings, setBrandSettings] = useState({
    instagramUrl: "",
    tiktokUrl: "",
    youtubeUrl: "",
    xUrl: ""
  });
  const [adminAlertSettings, setAdminAlertSettings] = useState({ userRegistered: false, cardAdded: false });
  const [scannerSettings, setScannerSettings] = useState({
    aiEnabled: false,
    provider: "openai",
    model: "gpt-4.1-mini",
    detail: "high",
    prompt: "",
    openAiApiKey: "",
    hasOpenAiKey: false,
    maskedOpenAiKey: "",
    estimatedScanCostMicros: 250
  });
  const [selectedBusinessId, setSelectedBusinessId] = useState("");
  const [moduleSavingKey, setModuleSavingKey] = useState("");
  const [businessModuleSavingKey, setBusinessModuleSavingKey] = useState("");
  const [reportStatus, setReportStatus] = useState("open");
  const [pendingReportDelete, setPendingReportDelete] = useState("");
  const [reportBusy, setReportBusy] = useState("");
  const [failedRegistrationBusy, setFailedRegistrationBusy] = useState("");
  const [diagnosticResults, setDiagnosticResults] = useState([]);
  const [diagnosticCheckedAt, setDiagnosticCheckedAt] = useState("");
  const [diagnosticBusy, setDiagnosticBusy] = useState("");
  const [inviteForm, setInviteForm] = useState({ email: "", inviteType: "user", message: "" });
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const loadAdminData = useCallback(() => {
    api.get("/analytics/platform").then(({ data }) => setMetrics(data)).catch(() => setMetrics(null));
    api.get("/admin/overview").then(({ data }) => setOverview(data)).catch(() => setOverview({ users: [], businesses: [], tournaments: [], invitations: [] }));
    api.get("/admin/operations").then(({ data }) => setOperationalAudits(data || { emails: [], deletions: [], failedRegistrations: [] })).catch(() => setOperationalAudits({ emails: [], deletions: [], failedRegistrations: [] }));
    api.get("/admin/modules").then(({ data }) => setModules(data || [])).catch(() => setModules([]));
    api.get("/admin/brand/settings").then(({ data }) => {
      setBrandSettings(current => ({ ...current, ...(data || {}) }));
    }).catch(() => {});
    api.get("/admin/alert-settings").then(({ data }) => setAdminAlertSettings(data || { userRegistered: false, cardAdded: false })).catch(() => {});
    api.get("/admin/card-scanner/settings").then(({ data }) => {
      setScannerSettings(current => ({ ...current, ...data, openAiApiKey: "" }));
    }).catch(() => {});
    api.get("/admin/card-scanner/data-center").then(({ data }) => setScannerDataCenter(data)).catch(() => setScannerDataCenter(null));
  }, []);


  const loadBusinessModules = useCallback((businessId = selectedBusinessId) => {
    if (!businessId) {
      setBusinessModules([]);
      return;
    }
    api
      .get(`/admin/businesses/${businessId}/modules`)
      .then(({ data }) => setBusinessModules(data || []))
      .catch(() => setBusinessModules([]));
  }, [selectedBusinessId]);

  useEffect(() => {
    if (!selectedBusinessId && overview.businesses?.length > 0) {
      setSelectedBusinessId(overview.businesses[0].id);
    }
  }, [overview.businesses, selectedBusinessId]);

  useEffect(() => {
    loadBusinessModules(selectedBusinessId);
  }, [loadBusinessModules, selectedBusinessId]);

  const loadUsers = useCallback(async (page = 1) => {
    setUsersLoading(true);
    try {
      const { data } = await api.get("/admin/users", { params: { ...appliedUserFilters, page, limit: 25 } });
      setUserDirectory(data);
    } catch (requestError) {
      setError(requestError?.response?.data?.error || "No se pudieron cargar los usuarios.");
    } finally {
      setUsersLoading(false);
    }
  }, [appliedUserFilters]);

  const loadReports = useCallback(() => {
    api.get("/admin/reports", { params: { status: reportStatus } }).then(({ data }) => setReports(data)).catch(() => setReports([]));
  }, [reportStatus]);

  useEffect(() => {
    loadAdminData();
  }, [loadAdminData]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  useEffect(() => {
    if (activeTab === "users") loadUsers(1);
  }, [activeTab, loadUsers]);

  const sendInvitation = async event => {
    event.preventDefault();
    setNotice("");
    setError("");
    try {
      const { data } = await api.post("/admin/invitations", inviteForm);
      setInviteForm({ email: "", inviteType: "user", message: "" });
      setNotice(data.delivery?.skipped
        ? `SMTP no configurado. Link generado: ${data.inviteUrl}`
        : "Invitacion enviada por email.");
      loadAdminData();
    } catch (requestError) {
      setError(requestError?.response?.data?.error || "No se pudo enviar la invitacion.");
    }
  };

  const updateReport = async (reportId, status, source) => {
    setReportBusy("review-" + reportId);
    try {
      await api.patch("/admin/reports/" + reportId, { status, source });
      setNotice(status === "dismissed" ? "Reporte descartado." : "Reporte marcado como revisado.");
      await loadReports();
    } catch (requestError) {
      setError(requestError?.response?.data?.error || "No se pudo actualizar el reporte.");
    } finally {
      setReportBusy("");
    }
  };

  const deleteReportedPost = async report => {
    if (!report.post_id || report.source !== "post") return;
    setReportBusy("delete-" + report.id);
    try {
      await api.delete("/feed/" + report.post_id);
      setPendingReportDelete("");
      setNotice("Publicacion eliminada por moderacion.");
      await loadReports();
    } catch (requestError) {
      setError(requestError?.response?.data?.error || "No se pudo eliminar la publicacion.");
    } finally {
      setReportBusy("");
    }
  };

  const updateBusinessModule = async (moduleKey, isEnabled) => {
    if (!selectedBusinessId) return;
    setNotice("");
    setError("");
    setBusinessModuleSavingKey(moduleKey);
    try {
      const { data } = await api.patch(`/admin/businesses/${selectedBusinessId}/modules/${moduleKey}`, { isEnabled });
      setBusinessModules(current => current.map(module => module.module_key === moduleKey ? { ...module, is_enabled: data.is_enabled } : module));
      setNotice("Modulo actualizado para la tienda.");
    } catch (requestError) {
      setError(requestError?.response?.data?.error || "No se pudo actualizar el modulo de la tienda.");
    } finally {
      setBusinessModuleSavingKey("");
    }
  };

  const updateModule = async (moduleKey, updates) => {
    setNotice("");
    setError("");
    setModuleSavingKey(moduleKey);
    try {
      const { data } = await api.patch(`/admin/modules/${moduleKey}`, updates);
      setModules(current => current.map(module => module.module_key === moduleKey ? { ...module, ...data } : module));
      setNotice("Modulo actualizado.");
    } catch (requestError) {
      setError(requestError?.response?.data?.error || "No se pudo actualizar el modulo.");
    } finally {
      setModuleSavingKey("");
    }
  };

  const saveBrandSettings = async event => {
    event.preventDefault();
    setNotice("");
    setError("");
    try {
      const { data } = await api.patch("/admin/brand/settings", brandSettings);
      setBrandSettings(current => ({ ...current, ...data }));
      setNotice("Redes de Cardora actualizadas.");
    } catch (requestError) {
      setError(requestError?.response?.data?.error || "No se pudieron guardar las redes.");
    }
  };

  const saveAdminAlertSettings = async event => {
    event.preventDefault();
    setNotice("");
    setError("");
    try {
      const { data } = await api.patch("/admin/alert-settings", adminAlertSettings);
      setAdminAlertSettings(data);
      setNotice("Alertas del superadmin actualizadas.");
    } catch (requestError) {
      setError(requestError?.response?.data?.error || "No se pudieron guardar las alertas.");
    }
  };

  const saveScannerSettings = async event => {
    event.preventDefault();
    setNotice("");
    setError("");
    try {
      const payload = {
        aiEnabled: Boolean(scannerSettings.aiEnabled),
        provider: scannerSettings.provider || "openai",
        model: scannerSettings.model || "gpt-4.1-mini",
        detail: scannerSettings.detail || "high",
        prompt: scannerSettings.prompt || "",
        estimatedScanCostMicros: Number(scannerSettings.estimatedScanCostMicros || 0)
      };
      if (scannerSettings.openAiApiKey?.trim()) payload.openAiApiKey = scannerSettings.openAiApiKey.trim();
      const { data } = await api.patch("/admin/card-scanner/settings", payload);
      setScannerSettings(current => ({ ...current, ...data, openAiApiKey: "" }));
      setNotice("Reconocimiento inteligente actualizado.");
    } catch (requestError) {
      setError(requestError?.response?.data?.error || "No se pudo guardar la configuracion de IA.");
    }
  };

  const runDiagnostic = async check => {
    setDiagnosticBusy(check);
    setError("");
    try {
      const { data } = await api.post("/admin/diagnostics/run", { check });
      setDiagnosticCheckedAt(data.checkedAt || new Date().toISOString());
      setDiagnosticResults(current => {
        if (check === "all") return data.results || [];
        const result = data.results?.[0];
        return result ? [...current.filter(item => item.key !== result.key), result] : current;
      });
    } catch (requestError) {
      setError(requestError?.response?.data?.error || "No se pudo ejecutar el diagnostico.");
    } finally {
      setDiagnosticBusy("");
    }
  };

  const dismissFailedRegistration = async id => {
    setFailedRegistrationBusy(id);
    setError("");
    try {
      await api.delete("/admin/operations/failed-registrations/" + id);
      setOperationalAudits(current => ({
        ...current,
        failedRegistrations: (current.failedRegistrations || []).filter(item => item.id !== id)
      }));
      setNotice("Correo descartado de la auditoria.");
    } catch (requestError) {
      setError(requestError?.response?.data?.error || "No se pudo descartar el correo.");
    } finally {
      setFailedRegistrationBusy("");
    }
  };

  return (
    <Container className="page-band store-page">
      <h1 className="section-title">Super admin</h1>
      <p className="muted-copy">Metricas, reportes, invitaciones y actividad general de Cardora.</p>

      {notice && <Alert variant="success" className="page-feedback">{notice}</Alert>}
      {error && <Alert variant="danger" className="page-feedback">{error}</Alert>}

      <Nav variant="tabs" activeKey={activeTab} onSelect={key => setActiveTab(key || "metrics")} className="binder-tabs mb-4">
        <Nav.Item><Nav.Link eventKey="metrics">Metricas</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="users">Usuarios</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="modules">Modulos</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="admin-alerts">Alertas admin</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="brand">Marca y redes</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="scanner">Data center</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="catalog">Catalogos</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="meetups">Juntadas</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="reports">Reportes</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="invitations">Invitaciones</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="communications">Comunicaciones</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="activity">Actividad</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="operations">Operacion</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="diagnostics">Diagnostico</Nav.Link></Nav.Item>
      </Nav>

      {activeTab === "users" && (
        <Row className="g-3">
          <Col xs={12}>
            <Card className="cardora-card">
              <Card.Body>
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
                  <div>
                    <Card.Title className="mb-1">Usuarios de Cardora</Card.Title>
                    <p className="muted-copy mb-0">{userDirectory.total} usuarios encontrados.</p>
                  </div>
                  <Badge bg="dark">{userDirectory.total}</Badge>
                </div>
                <Form className="row g-2 align-items-end mb-4" onSubmit={event => {
                  event.preventDefault();
                  setAppliedUserFilters({ search: userFilters.search.trim(), role: userFilters.role });
                }}>
                  <Form.Group className="col-12 col-md-7" controlId="admin-user-search">
                    <Form.Label>Buscar usuario</Form.Label>
                    <Form.Control value={userFilters.search} placeholder="Nombre, correo o usuario" onChange={event => setUserFilters(current => ({ ...current, search: event.target.value }))} />
                  </Form.Group>
                  <Form.Group className="col-8 col-md-3" controlId="admin-user-role">
                    <Form.Label>Rol</Form.Label>
                    <Form.Select value={userFilters.role} onChange={event => setUserFilters(current => ({ ...current, role: event.target.value }))}>
                      <option value="all">Todos</option>
                      <option value="user">Coleccionistas</option>
                      <option value="store_admin">Tiendas</option>
                      <option value="super_admin">Superadmins</option>
                    </Form.Select>
                  </Form.Group>
                  <div className="col-4 col-md-2 d-grid">
                    <Button type="submit" variant="dark" disabled={usersLoading}>
                      {usersLoading ? <Spinner size="sm" animation="border" /> : <i className="bi bi-search" />} <span className="ms-1 d-none d-sm-inline">Buscar</span>
                    </Button>
                  </div>
                </Form>
                <div className="admin-list">
                  {usersLoading && userDirectory.users.length === 0 && <div className="text-center py-4"><Spinner animation="border" /></div>}
                  {!usersLoading && userDirectory.users.length === 0 && <span className="muted-copy">No hay usuarios para estos filtros.</span>}
                  {userDirectory.users.map(user => (
                    <div className="admin-resource-row align-items-start" key={user.id}>
                      <div className="d-flex gap-3 align-items-start">
                        {user.avatar_url
                          ? <img src={mediaUrl(user.avatar_url)} alt="" width="42" height="42" className="rounded-circle object-fit-cover" />
                          : <span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-light border" style={{ width: 42, height: 42 }}><i className="bi bi-person" /></span>}
                        <div>
                          <strong>{user.display_name} {user.handle && <span className="muted-copy">@{user.handle}</span>}</strong>
                          <span>{user.email} · {[user.city, user.country].filter(Boolean).join(", ") || "Sin ubicacion"}</span>
                          <small className="muted-copy">{user.binder_count} binders · {user.card_count} cartas · {user.post_count} publicaciones</small>
                          {user.business_name && <small>Tienda: {user.business_name}</small>}
                        </div>
                      </div>
                      <div className="text-end">
                        <div className="d-flex gap-1 justify-content-end flex-wrap"><Badge bg={user.role === "super_admin" ? "danger" : user.role === "store_admin" ? "warning" : "secondary"} text={user.role === "store_admin" ? "dark" : undefined}>{user.role}</Badge><Badge bg={userActivation(user).bg} text={userActivation(user).text}>{userActivation(user).label}</Badge></div>
                        <small className={`d-block mt-1 ${user.email_verified_at ? "text-success" : "text-warning"}`}>{user.email_verified_at ? "Correo verificado" : "Correo pendiente"}</small>
                        <small className="d-block muted-copy">{new Date(user.created_at).toLocaleDateString("es-ES")}</small>
                          <Button as={Link} to={`/u/${user.handle || user.id}`} target="_blank" rel="noreferrer" size="sm" variant="link" className="px-0 mt-1">Ver perfil</Button>
                          <DeleteTestUser user={user} onDeleted={() => loadUsers(userDirectory.page)} />
                      </div>
                    </div>
                  ))}
                </div>
                {userDirectory.total > userDirectory.limit && (
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <Button size="sm" variant="outline-dark" disabled={usersLoading || userDirectory.page <= 1} onClick={() => loadUsers(userDirectory.page - 1)}><i className="bi bi-chevron-left" /> Anterior</Button>
                    <span className="muted-copy">Pagina {userDirectory.page} de {Math.ceil(userDirectory.total / userDirectory.limit)}</span>
                    <Button size="sm" variant="outline-dark" disabled={usersLoading || userDirectory.page * userDirectory.limit >= userDirectory.total} onClick={() => loadUsers(userDirectory.page + 1)}>Siguiente <i className="bi bi-chevron-right" /></Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {activeTab === "brand" && (
        <Row className="g-4">
          <Col xs={12}>
            <Card className="cardora-card">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                  <div>
                    <Card.Title className="mb-1">Registros que necesitan seguimiento</Card.Title>
                    <p className="muted-copy mb-0">Solo se conserva el correo. Descartalo cuando la persona haya podido registrarse.</p>
                  </div>
                  <Badge bg={(operationalAudits.failedRegistrations || []).length ? "warning" : "success"} text={(operationalAudits.failedRegistrations || []).length ? "dark" : undefined}>
                    {(operationalAudits.failedRegistrations || []).length} pendientes
                  </Badge>
                </div>
                <div className="admin-list">
                  {(operationalAudits.failedRegistrations || []).length === 0 && <span className="muted-copy">Sin registros fallidos pendientes.</span>}
                  {(operationalAudits.failedRegistrations || []).map(item => (
                    <div className="admin-resource-row" key={item.id}>
                      <div>
                        <strong>{item.email}</strong>
                        <span>Ultimo intento: {new Date(item.last_failed_at).toLocaleString("es-ES")} · {item.failure_count} {Number(item.failure_count) === 1 ? "fallo" : "fallos"}</span>
                      </div>
                      <Button size="sm" variant="outline-dark" disabled={failedRegistrationBusy === item.id} onClick={() => dismissFailedRegistration(item.id)}>
                        {failedRegistrationBusy === item.id ? <Spinner size="sm" animation="border" /> : <i className="bi bi-check2" />} <span className="ms-1">Resolver</span>
                      </Button>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={7}>
            <Card className="cardora-card">
              <Card.Body>
                <Card.Title>Marca y redes oficiales</Card.Title>
                <p className="muted-copy">
                  Los enlaces guardados aparecen automaticamente en el footer. Deja un campo vacio para ocultar esa red.
                </p>
                <Form className="admin-form-grid" onSubmit={saveBrandSettings}>
                  {[
                    ["instagramUrl", "Instagram", "https://instagram.com/cardora"],
                    ["tiktokUrl", "TikTok", "https://tiktok.com/@cardora"],
                    ["youtubeUrl", "YouTube", "https://youtube.com/@cardora"],
                    ["xUrl", "X", "https://x.com/cardora"]
                  ].map(([key, label, placeholder]) => (
                    <Form.Group controlId={`brand-${key}`} key={key}>
                      <Form.Label>{label}</Form.Label>
                      <Form.Control
                        type="url"
                        value={brandSettings[key] || ""}
                        placeholder={placeholder}
                        onChange={event => setBrandSettings(current => ({ ...current, [key]: event.target.value }))}
                      />
                    </Form.Group>
                  ))}
                  <Button type="submit" className="btn-cardora">Guardar redes</Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={5}>
            <Card className="cardora-card">
              <Card.Body>
                <Card.Title>Vista del footer</Card.Title>
                <p className="muted-copy mb-3">Solo se mostraran las redes que tengan una URL valida.</p>
                <div className="footer-socials admin-social-preview">
                  {[
                    ["Instagram", "instagramUrl", "bi-instagram"],
                    ["TikTok", "tiktokUrl", "bi-tiktok"],
                    ["YouTube", "youtubeUrl", "bi-youtube"],
                    ["X", "xUrl", "bi-twitter-x"]
                  ].filter(([, key]) => brandSettings[key]).map(([label, key, icon]) => (
                    <a href={brandSettings[key]} target="_blank" rel="noreferrer" aria-label={label} title={label} key={key}>
                      <i className={`bi ${icon}`} />
                    </a>
                  ))}
                  {!Object.entries(brandSettings).some(([key, value]) => key.endsWith("Url") && value) && (
                    <span className="muted-copy">Aun no hay redes configuradas.</span>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {activeTab === "admin-alerts" && (
        <Row className="g-3">
          <Col lg={7}>
            <Card className="cardora-card">
              <Card.Body>
                <Card.Title>Avisos de actividad</Card.Title>
                <p className="muted-copy">Recibe un correo en cada cuenta con rol superadmin. Los envios quedan auditados en Operacion.</p>
                <Form className="scanner-admin-settings" onSubmit={saveAdminAlertSettings}>
                  <Form.Check type="switch" id="admin-alert-user-registered" label="Avisar cuando se registra un usuario" checked={Boolean(adminAlertSettings.userRegistered)} onChange={event => setAdminAlertSettings(current => ({ ...current, userRegistered: event.target.checked }))} />
                  <Alert variant="light" className="mb-0"><i className="bi bi-collection me-2" />Las cartas agregadas se consultan en Operacion y no consumen correos.</Alert>
                  <Alert variant="light" className="mb-0"><i className="bi bi-envelope-check me-2" />Usa el SMTP configurado en el servidor. Las cartas marcadas solamente como buscadas no generan este aviso.</Alert>
                  <Button type="submit" className="btn-cardora" size="sm">Guardar alertas</Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {activeTab === "metrics" && (
        metrics ? <MetricsDashboard metrics={metrics} /> : <Card className="cardora-card"><Card.Body>No se pudieron cargar las metricas.</Card.Body></Card>
      )}


      {activeTab === "modules" && (
        <Row className="g-3">
          <Col lg={3}>
            <Card className="cardora-card h-100">
              <Card.Body>
                <Card.Title>Despliegue gradual</Card.Title>
                <p className="muted-copy">
                  Activa primero lo necesario para la tienda piloto. Lo que quede en later puede existir en codigo sin mostrarse como promesa del MVP.
                </p>
                <div className="admin-module-summary">
                  <span><strong>{modules.filter(module => module.is_enabled).length}</strong> activos globales</span>
                  <span><strong>{modules.filter(module => module.web_enabled).length}</strong> visibles web</span>
                  <span><strong>{modules.filter(module => module.mobile_enabled).length}</strong> preparados movil</span>
                </div>
                <hr />
                <Form className="scanner-admin-settings" onSubmit={saveScannerSettings}>
                  <div>
                    <strong>Reconocimiento inteligente</strong>
                    <p className="muted-copy mb-2">Activa Vision para leer nombre, numero y set desde la foto antes del matching local.</p>
                  </div>
                  <Form.Check
                    type="switch"
                    id="scanner-ai-enabled"
                    label="Usar IA en escaneo"
                    checked={Boolean(scannerSettings.aiEnabled)}
                    onChange={event => setScannerSettings(current => ({ ...current, aiEnabled: event.target.checked }))}
                  />
                  <Form.Select
                    value={scannerSettings.provider}
                    onChange={event => setScannerSettings(current => ({ ...current, provider: event.target.value }))}
                  >
                    <option value="openai">OpenAI Vision</option>
                  </Form.Select>
                  <Form.Control
                    value={scannerSettings.model}
                    onChange={event => setScannerSettings(current => ({ ...current, model: event.target.value }))}
                    placeholder="gpt-4.1-mini"
                  />
                  <Form.Select
                    value={scannerSettings.detail}
                    onChange={event => setScannerSettings(current => ({ ...current, detail: event.target.value }))}
                  >
                    <option value="low">Detalle bajo / menor coste</option>
                    <option value="high">Detalle alto</option>
                    <option value="auto">Automatico</option>
                    <option value="original">Original</option>
                  </Form.Select>
                  <Form.Control
                    type="password"
                    value={scannerSettings.openAiApiKey}
                    onChange={event => setScannerSettings(current => ({ ...current, openAiApiKey: event.target.value }))}
                    placeholder={scannerSettings.hasOpenAiKey ? `Clave guardada: ${scannerSettings.maskedOpenAiKey}` : "OpenAI API key"}
                    autoComplete="off"
                  />
                  <Form.Control
                    type="number"
                    min="0"
                    value={scannerSettings.estimatedScanCostMicros}
                    onChange={event => setScannerSettings(current => ({ ...current, estimatedScanCostMicros: event.target.value }))}
                    placeholder="Coste estimado por scan en micro USD"
                  />
                  <Form.Group controlId="scanner-ai-prompt">
                    <Form.Label>Instrucciones adicionales para el prompt base</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={8}
                      maxLength={12000}
                      value={scannerSettings.prompt}
                      onChange={event => setScannerSettings(current => ({ ...current, prompt: event.target.value }))}
                      placeholder="Ej.: Para Pokemon, prioriza distinguir cartas promocionales y variantes. Si el numero no es legible, usa null."
                    />
                    <Form.Text className="muted-copy">Se añade al prompt recomendado. Las reglas de JSON, datos no inventados y protección de cartas aprobadas no se pueden eliminar.</Form.Text>
                  </Form.Group>
                  <Form.Text className="muted-copy">250 micro USD = 0.00025 USD por analisis. Es estimado interno para MVP.</Form.Text>
                  <Button type="submit" className="btn-cardora" size="sm">Guardar IA</Button>
                </Form>
                <hr />
                <Form.Group>
                  <Form.Label>Tienda piloto</Form.Label>
                  <Form.Select
                    value={selectedBusinessId}
                    onChange={event => setSelectedBusinessId(event.target.value)}
                  >
                    {overview.businesses.map(business => (
                      <option value={business.id} key={business.id}>{business.name}</option>
                    ))}
                  </Form.Select>
                  <Form.Text className="muted-copy">
                    Los switches de tienda pisan el estado global solo para esa comunidad.
                  </Form.Text>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={8}>
            {selectedBusinessId && (
              <Card className="cardora-card admin-module-card mt-3">
                <Card.Body>
                  <div className="admin-module-head">
                    <div>
                      <Badge bg="light" text="dark">Por tienda</Badge>
                      <h2>Modulos de comunidad</h2>
                      <p>Activa o pausa funcionalidades para la tienda seleccionada sin afectar al resto.</p>
                    </div>
                  </div>
                  <div className="admin-module-switches business-module-grid">
                    {businessModules.map(module => {
                      const saving = businessModuleSavingKey === module.module_key;
                      const enabled = Boolean(module.is_enabled && module.web_enabled);
                      return (
                        <Form.Check
                          type="switch"
                          key={module.module_key}
                          id={`business-module-${module.module_key}`}
                          label={`${module.label} ${module.global_enabled ? "" : "(global pausado)"}`}
                          checked={enabled}
                          disabled={saving || !module.global_enabled || !module.web_enabled}
                          onChange={event => updateBusinessModule(module.module_key, event.target.checked)}
                        />
                      );
                    })}
                    {businessModules.length === 0 && <span className="muted-copy">Selecciona una tienda con modulos cargados.</span>}
                  </div>
                </Card.Body>
              </Card>
            )}

            <div className="admin-list">
              {modules.length === 0 && (
                <Card className="cardora-card"><Card.Body>No hay modulos cargados. Ejecuta la migracion 016.</Card.Body></Card>
              )}
              {modules.map(module => {
                const saving = moduleSavingKey === module.module_key;
                return (
                  <Card className="cardora-card admin-module-card" key={module.module_key}>
                    <Card.Body>
                      <div className="admin-module-head">
                        <div>
                          <Badge bg={module.rollout_stage === "pilot" ? "success" : "secondary"}>{module.rollout_stage}</Badge>
                          <h2>{module.label}</h2>
                          <p>{module.description}</p>
                          <small>{module.category} · {module.module_key}</small>
                        </div>
                        <Badge bg={module.is_enabled ? "success" : "light"} text={module.is_enabled ? undefined : "dark"}>
                          {module.is_enabled ? "Activo" : "Pausado"}
                        </Badge>
                      </div>
                      <div className="admin-module-switches">
                        <Form.Check
                          type="switch"
                          id={`module-global-${module.module_key}`}
                          label="Global"
                          checked={Boolean(module.is_enabled)}
                          disabled={saving}
                          onChange={event => updateModule(module.module_key, { isEnabled: event.target.checked })}
                        />
                        <Form.Check
                          type="switch"
                          id={`module-web-${module.module_key}`}
                          label="Web"
                          checked={Boolean(module.web_enabled)}
                          disabled={saving}
                          onChange={event => updateModule(module.module_key, { webEnabled: event.target.checked })}
                        />
                        <Form.Check
                          type="switch"
                          id={`module-mobile-${module.module_key}`}
                          label="Movil"
                          checked={Boolean(module.mobile_enabled)}
                          disabled={saving}
                          onChange={event => updateModule(module.module_key, { mobileEnabled: event.target.checked })}
                        />
                        <Form.Select
                          value={module.rollout_stage || "pilot"}
                          disabled={saving}
                          onChange={event => updateModule(module.module_key, { rolloutStage: event.target.value })}
                        >
                          <option value="pilot">Piloto</option>
                          <option value="later">Mas adelante</option>
                          <option value="disabled">No mostrar</option>
                        </Form.Select>
                      </div>
                    </Card.Body>
                  </Card>
                );
              })}
            </div>
          </Col>
        </Row>
      )}

      {activeTab === "scanner" && (
        <ScannerDataCenter data={scannerDataCenter} onRefresh={loadAdminData} />
      )}


      {activeTab === "catalog" && <CatalogManager />}
      {activeTab === "meetups" && <MeetupReviewManager />}

      {activeTab === "reports" && (
        <Card className="cardora-card">
          <Card.Body>
            <div className="d-flex flex-wrap justify-content-between gap-2 mb-3">
              <Card.Title>Reportes y seguridad</Card.Title>
              <Form.Select value={reportStatus} onChange={event => setReportStatus(event.target.value)} style={{ maxWidth: 220 }}>
                <option value="open">Abiertos</option>
                <option value="reviewed">Revisados</option>
                <option value="dismissed">Descartados</option>
                <option value="all">Todos</option>
              </Form.Select>
            </div>
            <div className="admin-list">
              {reports.length === 0 && <div className="muted-copy">No hay reportes con este filtro.</div>}
              {reports.map(report => (
                <div className="admin-resource-row" key={report.id}>
                  <div>
                    <strong>{report.post_type} · {report.author_name}</strong>
                    <span>{report.post_body || "Publicacion sin texto"}</span>
                    <small className="muted-copy d-block">Reportado por {report.reporter_name} · {new Date(report.created_at).toLocaleString("es-ES")}</small>
                    {report.business_slug && <Link to={`/store/${report.business_slug}`} className="small">Ver tienda</Link>}
                  </div>
                  <div className="admin-resource-actions">
                    <Badge bg={report.status === "open" ? "danger" : "secondary"}>{report.status}</Badge>
                    <Button size="sm" variant="outline-dark" disabled={Boolean(reportBusy)} onClick={() => updateReport(report.id, "reviewed", report.source)}>{reportBusy === "review-" + report.id && <Spinner size="sm" className="me-1" />}Revisado</Button>
                    <Button size="sm" variant="outline-secondary" disabled={Boolean(reportBusy)} onClick={() => updateReport(report.id, "dismissed", report.source)}>Descartar</Button>
                    {report.source === "post" && (pendingReportDelete === report.id
                      ? <span className="inline-confirm-actions"><span>¿Eliminar publicacion?</span><Button size="sm" variant="danger" disabled={reportBusy === "delete-" + report.id} onClick={() => deleteReportedPost(report)}>{reportBusy === "delete-" + report.id && <Spinner size="sm" className="me-1" />}Eliminar</Button><Button size="sm" variant="outline-dark" disabled={Boolean(reportBusy)} onClick={() => setPendingReportDelete("")}>Cancelar</Button></span>
                      : <Button size="sm" variant="outline-danger" disabled={Boolean(reportBusy)} onClick={() => setPendingReportDelete(report.id)}><i className="bi bi-trash me-1" />Borrar post</Button>)}
                  </div>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      {activeTab === "communications" && <AdminBroadcastManager />}

      {activeTab === "invitations" && (
        <Row className="g-4">
          <Col lg={5}>
            <Card className="cardora-card h-100">
              <Card.Body>
                <Card.Title>Enviar invitacion</Card.Title>
                <Form className="admin-form-grid" onSubmit={sendInvitation}>
                  <Form.Control
                    type="email"
                    placeholder="email@dominio.com"
                    value={inviteForm.email}
                    onChange={event => setInviteForm({ ...inviteForm, email: event.target.value })}
                    required
                  />
                  <Form.Select value={inviteForm.inviteType} onChange={event => setInviteForm({ ...inviteForm, inviteType: event.target.value })}>
                    <option value="user">Usuario coleccionista</option>
                    <option value="business">Tienda/comunidad</option>
                  </Form.Select>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="Mensaje opcional"
                    value={inviteForm.message}
                    onChange={event => setInviteForm({ ...inviteForm, message: event.target.value })}
                  />
                  <Button type="submit" className="btn-cardora">Enviar invitacion</Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={7}>
            <Card className="cardora-card h-100">
              <Card.Body>
                <Card.Title>Ultimas invitaciones</Card.Title>
                <div className="admin-list">
                  {overview.invitations?.length === 0 && <div className="muted-copy">Todavia no hay invitaciones.</div>}
                  {overview.invitations?.map(invitation => (
                    <div className="admin-resource-row" key={invitation.id}>
                      <div>
                        <strong>{invitation.email}</strong>
                        <span>{invitation.invite_type} · {invitation.message || "sin mensaje"}</span>
                      </div>
                      <Badge bg={invitation.status === "sent" ? "success" : "warning"} text={invitation.status === "pending" ? "dark" : undefined}>
                        {invitation.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {activeTab === "activity" && (
        <Row className="g-4">
          <Col lg={3}><ActivityCard title="Usuarios" items={overview.users} render={user => `${user.display_name} · ${user.role}`} /></Col>
          <Col lg={3}><ActivityCard title="Tiendas" items={overview.businesses} render={business => `${business.name} · ${business.verification_status}`} /></Col>
          <Col lg={3}><ActivityCard title="Eventos" items={overview.tournaments} render={tournament => `${tournament.name} · ${tournament.status}`} /></Col>
        </Row>
      )}
      {activeTab === "diagnostics" && (
        <Row className="g-3">
          <Col xs={12}>
            <Card className="cardora-card">
              <Card.Body>
                <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
                  <div>
                    <Card.Title className="mb-1">Centro de diagnostico</Card.Title>
                    <p className="muted-copy mb-0">Comprueba los servicios esenciales sin crear usuarios reales ni enviar correos.</p>
                  </div>
                  <Button className="btn-cardora" disabled={Boolean(diagnosticBusy)} onClick={() => runDiagnostic("all")}>
                    {diagnosticBusy === "all" ? <Spinner size="sm" animation="border" /> : <i className="bi bi-play-fill" />} <span className="ms-1">Ejecutar todo</span>
                  </Button>
                </div>
                <Alert variant="light" className="border mb-3">
                  El registro sintetico siempre se revierte. SMTP solo valida la conexion y almacenamiento elimina su archivo de prueba.
                </Alert>
                <div className="d-flex flex-wrap gap-2 mb-4">
                  {[
                    ["database", "Base de datos", "bi-database"],
                    ["migrations", "Migraciones", "bi-layers"],
                    ["registration", "Registro", "bi-person-check"],
                    ["smtp", "SMTP", "bi-envelope-check"],
                    ["storage", "Imagenes", "bi-cloud-check"]
                  ].map(([key, label, icon]) => (
                    <Button key={key} size="sm" variant="outline-dark" disabled={Boolean(diagnosticBusy)} onClick={() => runDiagnostic(key)}>
                      {diagnosticBusy === key ? <Spinner size="sm" animation="border" /> : <i className={`bi ${icon}`} />} <span className="ms-1">{label}</span>
                    </Button>
                  ))}
                </div>
                <div className="admin-list">
                  {diagnosticResults.length === 0 && <span className="muted-copy">Ejecuta las comprobaciones para ver el estado actual.</span>}
                  {diagnosticResults.map(result => (
                    <div className="admin-resource-row" key={result.key}>
                      <div>
                        <strong>{({ database: "Base de datos", migrations: "Migraciones", registration: "Registro", smtp: "Correo SMTP", storage: "Almacenamiento" })[result.key] || result.key}</strong>
                        <span>{result.message}</span>
                        {result.details?.pending?.length > 0 && <small className="text-danger">Pendientes: {result.details.pending.join(", ")}</small>}
                      </div>
                      <div className="text-end">
                        <Badge bg={result.ok ? "success" : "danger"}>{result.ok ? "Correcto" : "Revisar"}</Badge>
                        {Number.isFinite(result.durationMs) && <small className="d-block muted-copy mt-1">{result.durationMs} ms</small>}
                      </div>
                    </div>
                  ))}
                </div>
                {diagnosticCheckedAt && <small className="muted-copy d-block mt-3">Ultima comprobacion: {new Date(diagnosticCheckedAt).toLocaleString("es-ES")}</small>}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {activeTab === "operations" && (
        <Row className="g-4">
          <Col lg={12}>
            <Card className="cardora-card">
              <Card.Body>
                <Card.Title>Cartas agregadas recientemente</Card.Title>
                <div className="admin-list">
                  {(!operationalAudits.recentCards || operationalAudits.recentCards.length === 0) && <span className="muted-copy">Sin actividad reciente.</span>}
                  {(operationalAudits.recentCards || []).map(card => (
                    <div className="admin-resource-row" key={card.id}>
                      <div><strong>{card.card_name}</strong><span>{card.display_name || card.email} · {card.set_name} · {new Date(card.created_at).toLocaleString("es-ES")}</span></div>
                      <Badge bg="light" text="dark">x{card.quantity || 1}</Badge>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={7}>
            <Card className="cardora-card">
              <Card.Body>
                <Card.Title>Entregas de email</Card.Title>
                <div className="admin-list">
                  {operationalAudits.emails.length === 0 && <span className="muted-copy">Sin entregas auditadas.</span>}
                  {operationalAudits.emails.map(email => (
                    <div className="admin-resource-row" key={email.id}>
                      <div>
                        <strong>{email.subject}</strong>
                        <span>{email.recipient} - {email.notification_type}</span>
                        {email.last_error && <small className="text-danger">{email.last_error}</small>}
                      </div>
                      <Badge bg={email.status === "sent" ? "success" : email.status === "failed" ? "danger" : "secondary"}>{email.status}</Badge>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={5}>
            <Card className="cardora-card">
              <Card.Body>
                <Card.Title>Eliminacion de cuentas</Card.Title>
                <div className="admin-list">
                  {operationalAudits.deletions.length === 0 && <span className="muted-copy">Sin solicitudes.</span>}
                  {operationalAudits.deletions.map(request => (
                    <div className="admin-resource-row" key={request.id}>
                      <div>
                        <strong>{request.display_name || request.email}</strong>
                        <span>Programada: {new Date(request.scheduled_for).toLocaleDateString("es-ES")}</span>
                      </div>
                      <Badge bg={request.status === "pending" ? "warning" : "secondary"} text={request.status === "pending" ? "dark" : undefined}>{request.status}</Badge>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

    </Container>
  );
}




function ActivityCard({ title, items, render }) {
  return (
    <Card className="cardora-card h-100">
      <Card.Body>
        <Card.Title>{title}</Card.Title>
        <div className="admin-list">
          {items?.length === 0 && <div className="muted-copy">Sin datos.</div>}
          {items?.map(item => (
            <div className="admin-resource-row" key={item.id}>
              <div>
                <strong>{render(item)}</strong>
                <span>{item.created_at ? new Date(item.created_at).toLocaleDateString("es-ES") : "Sin fecha"}</span>
              </div>
            </div>
          ))}
        </div>
      </Card.Body>
    </Card>
  );
}

export default SuperAdminDashboard;
